import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useCurrentRoles } from 'hooks/use-current-roles';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { HP_LOGO_URL, showDesktopNotice } from 'pages/support-shared/desktop-notify';
import { playChime, readSoundPref } from 'pages/support-shared/utils';

type AlertItem = {
  id: number; npcName: string; location: string; reasonCode: string;
  routeLevel: number; targetUserId: number; status: string;
};

/** Trang quản trị còn mở → quản lý trực được phân công cũng nghe chuông ở mọi trang. */
const LiveSupportNotifier = () => {
  const { userId } = useCurrentRoles();
  const navigate = useNavigate();
  const me = useRef(userId);
  const navigation = useRef(navigate);
  me.current = userId;
  navigation.current = navigate;

  useEffect(() => {
    let running = false;
    let stopped = false;
    const tick = async () => {
      if (running || stopped || !me.current) return;
      running = true;
      try {
        const res: any = await AxiosUtils.get(API.liveSupportAdmin + '/alerts');
        const mine: AlertItem[] = (res.data || []).filter((r: AlertItem) =>
          r.status === 'NEW' && Number(r.targetUserId) === me.current && Number(r.routeLevel) === 2);
        mine.forEach((r) => {
          const key = `live-support-manager.${me.current}.${r.id}.${r.routeLevel}`;
          try {
            if (localStorage.getItem(key)) return;
            localStorage.setItem(key, '1');
          } catch { /* thông báo vẫn hiện nếu trình duyệt chặn bộ nhớ */ }
          const title = `${r.npcName} cần KTV · ${r.location}`;
          const body = `Quá hạn nhận · ${r.reasonCode}. Bạn đang là quản lý trực.`;
          const open = () => navigation.current('/live-management?tab=support');
          const sound = readSoundPref();
          const canPlay = !!(navigator as any).userActivation?.hasBeenActive;
          const shown = showDesktopNotice({ title, body, tag: `live-support-${r.id}`,
            silent: sound && canPlay, onClick: open });
          if (sound && (canPlay || !shown)) playChime();
          if (!shown) notification.open({ key, placement: 'bottomRight',
            icon: <img src={HP_LOGO_URL} alt="HP" width={30} height={30} />,
            message: title, description: body, duration: 10,
            onClick: open, style: { cursor: 'pointer' } });
        });
      } catch {
        // Server chưa chạy migration / tạm mất mạng → lần sau thử lại.
      } finally { running = false; }
    };
    // Timer trong Worker để tab nền không bị giảm nhịp; không có Worker thì dùng timer thường.
    let stop: () => void;
    try {
      const url = URL.createObjectURL(new Blob(['setInterval(function(){postMessage(0)},8000)'], { type: 'text/javascript' }));
      const worker = new Worker(url);
      worker.onmessage = tick;
      stop = () => { worker.terminate(); URL.revokeObjectURL(url); };
    } catch {
      const id = window.setInterval(tick, 8000);
      stop = () => window.clearInterval(id);
    }
    tick();
    return () => { stopped = true; stop(); };
  }, []);
  return null;
};

export default LiveSupportNotifier;
